import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Slider } from "../components/ui/slider";
import { TrendingUp, TrendingDown, IndianRupee, BarChart3, Percent } from "lucide-react";
import { calculateOurOfferMetrics } from "../utils/calculations";

const formatCr = (val) => `${(val / 10000000).toFixed(2)} Cr`;
const formatInr = (val) => `${parseInt(val).toLocaleString("en-IN")}`;

const Analysis = ({ formData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts, plData }) => {
  const baseFreeArea = parseFloat(formData.freeAreaPercentFlats) || parseFloat(formData.additionalFreeAreaPercent) || 0;
  const baseBetterment = parseFloat(formData.bettermentCharges) || 0;
  const baseCCost = parseFloat(formData.constructionCostPerSqft) || 0;
  const baseRRRate = parseFloat(formData.rrRatePlot) || 0;
  const baseSaleRate = parseFloat(formData.saleRateFlats) || parseFloat(formData.saleRatePerSqft) || 0;

  const [overrides, setOverrides] = useState({
    freeAreaPercent: baseFreeArea,
    bettermentFlats: baseBetterment,
    constructionCost: baseCCost,
    rrRate: baseRRRate,
    saleRate: baseSaleRate,
  });

  const baseMetrics = plData;

  const simMetrics = useMemo(() => {
    const simFormData = {
      ...formData,
      freeAreaPercentFlats: String(overrides.freeAreaPercent),
      freeAreaPercentOffices: String(overrides.freeAreaPercent),
      freeAreaPercentShops: String(overrides.freeAreaPercent),
      additionalFreeAreaPercent: String(overrides.freeAreaPercent),
      bettermentCharges: String(overrides.bettermentFlats),
      constructionCostPerSqft: String(overrides.constructionCost),
      rrRatePlot: String(overrides.rrRate),
      saleRateFlats: String(overrides.saleRate),
      saleRatePerSqft: String(overrides.saleRate),
    };

    // Recalculate FSI costs with new RR rate
    const rrMultPercent = parseFloat(formData.rrMultiplier) || 5;
    const rrMarkup = formData.applyRRMarkup === "yes" ? (parseFloat(formData.rrAdditionalMarkupPercent) || 0) : 0;
    const newEffectiveRR = overrides.rrRate * (1 + rrMultPercent / 100) * (1 + rrMarkup / 100);
    const oldEffectiveRR = baseRRRate * (1 + rrMultPercent / 100) * (1 + rrMarkup / 100);
    const rrRatio = oldEffectiveRR > 0 ? newEffectiveRR / oldEffectiveRR : 1;

    const simFsiCosts = fsiCosts ? {
      ...fsiCosts,
      amenityTDRCost: (fsiCosts.amenityTDRCost || 0) * rrRatio,
      slumTDRCost: fsiCosts.slumTDRMethod === "zone_drc" ? (fsiCosts.slumTDRCost || 0) : (fsiCosts.slumTDRCost || 0) * rrRatio,
      paidFSICost: (fsiCosts.paidFSICost || 0) * rrRatio,
      ancillaryFSICost: (fsiCosts.ancillaryFSICost || 0) * rrRatio,
      totalCosts: (fsiCosts.totalCosts || 0) + ((fsiCosts.amenityTDRCost || 0) + (fsiCosts.paidFSICost || 0) + (fsiCosts.ancillaryFSICost || 0) + (fsiCosts.slumTDRMethod !== "zone_drc" ? (fsiCosts.slumTDRCost || 0) : 0)) * (rrRatio - 1),
    } : null;

    // Recalculate society costs with new betterment
    const numMembers = parseInt(formData.numMembers) || 0;
    const bettermentDiff = (overrides.bettermentFlats - baseBetterment) * numMembers;
    const simSocietyCosts = societyCosts ? {
      ...societyCosts,
      bettermentCharges: overrides.bettermentFlats * numMembers,
      totalSocietyExpenses: (societyCosts.totalSocietyExpenses || 0) + bettermentDiff,
    } : null;

    return calculateOurOfferMetrics(simFormData, fsiBreakdown, simFsiCosts, daChargesCosts, simSocietyCosts);
  }, [overrides, formData, fsiBreakdown, fsiCosts, daChargesCosts, societyCosts, baseBetterment, baseRRRate]);

  const baseProfit = baseMetrics?.grossProfit || 0;
  const simProfit = simMetrics?.grossProfit || 0;
  const profitDelta = simProfit - baseProfit;
  const isPositive = profitDelta >= 0;

  const handleChange = (field, value) => {
    setOverrides((prev) => ({ ...prev, [field]: value }));
  };

  const handleReset = () => {
    setOverrides({
      freeAreaPercent: baseFreeArea,
      bettermentFlats: baseBetterment,
      constructionCost: baseCCost,
      rrRate: baseRRRate,
      saleRate: baseSaleRate,
    });
  };

  const fields = [
    { key: "freeAreaPercent", label: "Free Area %", base: baseFreeArea, step: 0.5, min: 0, max: 100, suffix: "%", icon: Percent },
    { key: "bettermentFlats", label: "Betterment - Flats (per flat)", base: baseBetterment, step: 10000, min: 0, max: 5000000, prefix: "₹", icon: IndianRupee },
    { key: "constructionCost", label: "Construction Cost", base: baseCCost, step: 100, min: 0, max: 20000, suffix: "/sft", prefix: "₹", icon: BarChart3 },
    { key: "rrRate", label: "RR Rate (Plot)", base: baseRRRate, step: 1000, min: 0, max: 500000, prefix: "₹", suffix: "/sq m", icon: IndianRupee },
    { key: "saleRate", label: "Sale Rate (Flats)", base: baseSaleRate, step: 500, min: 0, max: 100000, prefix: "₹", suffix: "/sft", icon: TrendingUp },
  ];

  return (
    <div className="space-y-6" data-testid="analysis-page">
      {/* Results Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className={`rounded-sm border-2 ${isPositive ? "border-green-400 bg-green-50" : "border-red-400 bg-red-50"}`}>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground font-medium mb-1">Simulated Profit</p>
            <p className={`text-xl font-bold ${simProfit >= 0 ? "text-green-700" : "text-red-700"}`} data-testid="sim-profit">
              {formatCr(simProfit)}
            </p>
          </CardContent>
        </Card>
        <Card className={`rounded-sm border-2 ${isPositive ? "border-green-300" : "border-red-300"}`}>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground font-medium mb-1">Change from Base</p>
            <div className="flex items-center justify-center gap-1">
              {isPositive ? <TrendingUp className="h-4 w-4 text-green-600" /> : <TrendingDown className="h-4 w-4 text-red-600" />}
              <p className={`text-xl font-bold ${isPositive ? "text-green-700" : "text-red-700"}`} data-testid="profit-delta">
                {isPositive ? "+" : ""}{formatCr(profitDelta)}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-sm">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground font-medium mb-1">Revenue</p>
            <p className="text-xl font-bold text-blue-700" data-testid="sim-revenue">{formatCr(simMetrics?.totalRevenue || 0)}</p>
          </CardContent>
        </Card>
        <Card className="rounded-sm">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground font-medium mb-1">Margin</p>
            <p className={`text-xl font-bold ${(simMetrics?.profitMarginPercent || 0) >= 0 ? "text-green-700" : "text-red-700"}`} data-testid="sim-margin">
              {(simMetrics?.profitMarginPercent || 0).toFixed(1)}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Base vs Simulated Comparison */}
      <Card className="rounded-sm">
        <CardHeader className="pb-3 bg-slate-50 border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-heading">Base vs Simulated</CardTitle>
            <button onClick={handleReset} className="text-xs text-blue-600 hover:underline font-medium" data-testid="reset-btn">
              Reset All
            </button>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-4 gap-2 text-xs font-semibold text-muted-foreground border-b pb-2 mb-2">
            <span>Metric</span><span className="text-right">Base</span><span className="text-right">Simulated</span><span className="text-right">Delta</span>
          </div>
          {[
            { label: "Revenue", base: baseMetrics?.totalRevenue, sim: simMetrics?.totalRevenue },
            { label: "FSI Costs", base: baseMetrics?.fsiTotalCosts, sim: simMetrics?.fsiTotalCosts },
            { label: formData.projectMode === "greenfield" ? "JV Costs" : "Society Exp", base: baseMetrics?.totalSocietyExpenses, sim: simMetrics?.totalSocietyExpenses },
            { label: "Construction", base: baseMetrics?.totalConstructionCost, sim: simMetrics?.totalConstructionCost },
            { label: "Total Expenses", base: baseMetrics?.totalExpenses, sim: simMetrics?.totalExpenses },
            { label: "Profit", base: baseMetrics?.grossProfit, sim: simMetrics?.grossProfit },
          ].map((row) => {
            const d = (row.sim || 0) - (row.base || 0);
            return (
              <div key={row.label} className="grid grid-cols-4 gap-2 text-sm py-1.5 border-b border-dashed last:border-0">
                <span className="font-medium">{row.label}</span>
                <span className="text-right">{formatCr(row.base || 0)}</span>
                <span className="text-right">{formatCr(row.sim || 0)}</span>
                <span className={`text-right font-semibold ${d >= 0 ? "text-green-600" : "text-red-600"}`}>
                  {d >= 0 ? "+" : ""}{formatCr(d)}
                </span>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Editable Fields */}
      <div className="grid md:grid-cols-1 gap-4">
        {fields.map((f) => {
          const delta = f.key === "freeAreaPercent"
            ? overrides[f.key] - f.base
            : overrides[f.key] - f.base;
          const changed = Math.abs(delta) > 0.001;
          return (
            <Card key={f.key} className={`rounded-sm transition-all ${changed ? "border-amber-400 bg-amber-50/30" : ""}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <f.icon className="h-4 w-4 text-muted-foreground" />
                    <Label className="font-semibold text-sm">{f.label}</Label>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">Base: {f.prefix || ""}{formatInr(f.base)}{f.suffix || ""}</span>
                    <Input
                      type="number"
                      value={overrides[f.key]}
                      onChange={(e) => handleChange(f.key, parseFloat(e.target.value) || 0)}
                      className="w-32 h-8 text-right text-sm rounded-sm"
                      step={f.step}
                      data-testid={`analysis-${f.key}-input`}
                    />
                  </div>
                </div>
                <Slider
                  value={[overrides[f.key]]}
                  onValueChange={([v]) => handleChange(f.key, v)}
                  min={f.min}
                  max={f.max}
                  step={f.step}
                  className="mt-1"
                  data-testid={`analysis-${f.key}-slider`}
                />
                {changed && (
                  <p className={`text-xs mt-2 font-medium ${delta > 0 ? (f.key === "saleRate" ? "text-green-600" : "text-red-600") : (f.key === "saleRate" ? "text-red-600" : "text-green-600")}`}>
                    {delta > 0 ? "+" : ""}{f.key === "freeAreaPercent" ? delta.toFixed(1) + "%" : f.prefix + formatInr(delta) + (f.suffix || "")} from base
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default Analysis;
